<template>
    <Layout is-crm content-class="content--narrow">
        <div v-if="order.id" class="order">
            <Spinner v-if="loading || !order.id" />
            <h1 class="order__title">{{ order.details.name }}</h1>
            <div class="order__top">
                <div>
                    <div class="order__id">{{ order.id }}</div>
                    <div class="order__created">
                        {{ formatDate(order.created, { locale: $i18n.locale }) }}
                    </div>
                </div>
                <div>
                    <OrderStatusBadge v-if="order.state.label" class="order__state">
                        {{ order.state.label[$i18n.locale] }}
                    </OrderStatusBadge>
                    <OrderMenu :items="menuItems" />
                </div>
            </div>
            <Grid>
                <h4>Order Info</h4>
                <button
                    v-if="order.permissions.partial_update && !editMode"
                    class="button button--blank order__edit-trigger"
                    type="button"
                    @click="editMode = true"
                >
                    Edit
                </button>
            </Grid>
            <template v-if="!editMode">
                <div class="order__info">
                    <Grid>
                        <Column :span="8">
                            <template v-if="isAdmin">
                                <div class="descriptions">
                                    <div class="descriptions__label">Customer:</div>
                                    <div class="descriptions__value">
                                        {{ order.customer.name }}
                                    </div>
                                </div>
                                <div class="descriptions">
                                    <div class="descriptions__label">Type:</div>
                                    <div class="descriptions__value">
                                        {{ order.type_productions }}
                                    </div>
                                </div>
                                <br />
                            </template>
                            <div class="descriptions">
                                <div class="descriptions__label">Title:</div>
                                <div class="descriptions__value">{{ order.details.name }}</div>
                            </div>
                            <div class="descriptions">
                                <div class="descriptions__label">Author(s):</div>
                                <div class="descriptions__value">{{ authors }}</div>
                            </div>
                            <OrderRoles v-if="order.personages.length" :roles="order.personages" />
                            <div v-if="order.details.isbnx" class="descriptions">
                                <div class="descriptions__label">ISBN:</div>
                                <div class="descriptions__value">{{ order.details.isbnx }}</div>
                            </div>
                            <div v-if="order.details.pause_config" class="descriptions">
                                <div class="descriptions__label">Pause config:</div>
                                <div class="descriptions__value">
                                    {{ order.details.pause_config.label }}
                                </div>
                            </div>
                            <div v-if="order.details.comment" class="order__description">
                                {{ order.details.comment }}
                            </div>
                        </Column>
                        <Column :span="4">
                            <div
                                class="descriptions descriptions--vertical descriptions--align-right"
                            >
                                <div class="descriptions__label">ETA:</div>
                                <div class="descriptions__value">
                                    {{ order.eta ? formatDate(order.eta) : 'N/A' }}
                                </div>
                            </div>
                        </Column>
                    </Grid>
                </div>
                <div class="order__actions">
                    <Grid v-if="order">
                        <Column :span="6">
                            <OrderBookBuilds class="order__books-builds" :order="order" />
                        </Column>
                    </Grid>
                    <Grid>
                        <Column v-if="order.remote_file_link" :span="6">
                            <div class="order__action">
                                <router-link
                                    :to="{ name: 'orders-id-document', params: { id: order.id } }"
                                    class="button button--outlined button--fullwidth button--medium"
                                >
                                    Edit Text
                                </router-link>
                            </div>
                        </Column>
                        <Column v-if="order.source_file_link" :span="6">
                            <div class="order__action">
                                <router-link
                                    :to="{ name: 'orders-id-source', params: { id: order.id } }"
                                    class="button button--outlined button--fullwidth button--medium"
                                >
                                    View the file
                                </router-link>
                            </div>
                        </Column>
                        <Column v-if="order.permissions.guest_token" :span="6">
                            <div class="order__action">
                                <button
                                    type="button"
                                    class="button button--outlined button--fullwidth button--medium"
                                    @click="getEmbedLink"
                                >
                                    Go to Editor
                                </button>
                                <a v-show="0" ref="embedLinkRes" :href="embedLink"></a>
                            </div>
                        </Column>
                        <Column v-if="order.permissions.workers" :span="6">
                            <OrderManageWorkers
                                v-if="
                                    order.manage_worker_info && order.manage_worker_info.can_manage
                                "
                                ref="orderWorkers"
                                :order="order"
                            >
                                <template #default="{ open }">
                                    <div class="order__action">
                                        <button
                                            type="button"
                                            class="button button--outlined button--fullwidth button--medium"
                                            @click="open"
                                        >
                                            Manage workers
                                        </button>
                                    </div>
                                </template>
                            </OrderManageWorkers>
                        </Column>
                    </Grid>
                </div>

                <OrderHistory :history="history" :order="order" class="order__history" />
                <div v-if="order.state.description" class="order__actions">
                    <h4>{{ order.state.description[$i18n.locale] }}</h4>
                </div>
                <div v-if="comments.length" class="order__comments">
                    <p v-for="(comment, i) in comments" :key="i">— {{ comment }}</p>
                </div>
                <div
                    v-if="order.state.slug === 'waiting_file' && order.permissions.partial_update"
                    class="order__actions"
                >
                    <p>
                        The book may contain tables and schemes which cannot be voiced. Please
                        remove or rewrite the material and upload a new file
                    </p>
                    <!-- todo this form needs to have destination state slug -->
                    <FileInput
                        ref="fileInput"
                        style="width: 100%"
                        accept=".docx, .epub, .fb2, .rtf"
                        label="Or upload the book in .docx or .epub, .fb2, .rtf"
                        with-link
                        @selected="setFile"
                        @update:link="setFile"
                    />
                </div>
                <div
                    v-if="order.transitions.length && order.permissions.change_state"
                    class="order__actions"
                >
                    <Grid>
                        <Column
                            v-for="transition in order.transitions"
                            :key="transition.destination_state"
                            :span="6"
                        >
                            <button
                                type="button"
                                class="button button--fullwidth button--medium"
                                :disabled="
                                    (order.state.slug === 'waiting_file' && !newFile) ||
                                    !transition.is_accept
                                "
                                @click="activeTransition = transition"
                            >
                                {{ transition.action_text[$i18n.locale] }}
                            </button>
                        </Column>
                    </Grid>
                </div>
            </template>
            <div v-else class="order__edit">
                <OrderEditForm
                    v-model="order"
                    :schema="schema"
                    @cancel="updateOrder"
                    @save="updateOrder"
                />
            </div>
        </div>
        <div
            v-if="'permissions' in order && order.permissions.get_comments"
            class="order__comments-wrapper"
        >
            <OrderComments :order-id="order.id" :can-create="order.permissions.create_comment" />
        </div>
        <ConfirmModal
            v-if="activeTransition"
            :title="activeTransition.action_text[$i18n.locale]"
            :ok-label="$t('misc.confirm')"
            :ok-disabled="!additionalDataValid"
            @confirmed="changeState(activeTransition)"
            @canceled="activeTransition = null"
        >
            <template #content>
                <Spinner v-if="stateLoading" />
                {{ activeTransition.description[$i18n.locale] }}
                <form v-if="activeTransition.schema" class="order__additional-data">
                    <FormField
                        v-for="(field, name) in activeTransition.schema"
                        :key="name"
                        :name="name"
                        :label="field.label"
                        :required="field.required"
                    >
                        <div>
                            <div
                                v-if="name in preparedOptions"
                                class="order__additional-data-options"
                            >
                                <div v-for="(variant, i) in preparedOptions[name]" :key="i">
                                    <Checkbox v-model="additionalDataVariants" :value="variant">
                                        {{ variant }}
                                    </Checkbox>
                                </div>
                            </div>
                            <div>
                                <FormTextarea v-model="additionalData[name]" />
                            </div>
                        </div>
                    </FormField>
                </form>
            </template>
        </ConfirmModal>
    </Layout>
</template>

<script>
import {
    fetchGuestToken,
    fetchOrder,
    fetchOrderSchema,
    changeState,
    fetchOrderHistory,
    updateOrder,
} from '../../api/orders';
import { fetchPreparedOptions } from '../../../utils/api/statemachine';
import { Grid, Column } from '@speechki-frontend/shared/ui/Grid';
import Layout from '@speechki-frontend/shared/ui/layout/Layout';
import Spinner from '@speechki-frontend/shared/ui/Spinner';
import {
    OrderMenu,
    OrderStatusBadge,
    OrderEditForm,
    OrderHistory,
    OrderManageWorkers,
    OrderBookBuilds,
    OrderComments,
    OrderRoles,
} from '../../components';
import { mapActions, mapGetters, mapState } from 'vuex';
import { formatDate } from '@speechki-frontend/shared/helpers/functions';
import { FileInput, FormTextarea, FormField, Checkbox } from '@speechki-frontend/shared/ui/forms';
import { ConfirmModal } from '@speechki-frontend/shared/ui/modals';

const embedURL = process.env.VUE_APP_EMBED_URL;

export default {
    name: 'OrderDetailPage',
    components: {
        Layout,
        Spinner,
        FileInput,
        ConfirmModal,
        Grid,
        Column,
        FormField,
        FormTextarea,
        Checkbox,

        OrderStatusBadge,
        OrderMenu,
        OrderEditForm,
        OrderHistory,
        OrderManageWorkers,
        OrderBookBuilds,
        OrderComments,
        OrderRoles,
    },

    inject: ['registerWsHandler', 'removeWsHandler'],

    data() {
        return {
            order: {},
            schema: {},
            loading: false,
            stateLoading: false,
            activeTransition: null,
            additionalData: {},
            additionalDataVariants: [],
            preparedOptions: {},
            history: [],
            editMode: false,

            newFile: '',
            embedLink: null,

            newFileApproved: false,
            refetchedContent: false,
            fetchIntervalTimer: 0,
            fetchIntervalDelay: 0,
        };
    },

    computed: {
        ...mapGetters(['isAdmin']),
        ...mapState({
            user: (state) => state.auth.user,
        }),
        permissions() {
            return this.order.permissions;
        },
        authors() {
            return this.order.details.raw_authors.map((a) => a.full_name).join(', ');
        },
        additionalDataValid() {
            return (
                !this.activeTransition ||
                !this.activeTransition.schema ||
                this.additionalDataVariants.length ||
                Object.entries(this.activeTransition.schema).every(([name, field]) => {
                    return !field.required || !!this.additionalData[name];
                })
            );
        },
        comments() {
            const { comment = '' } = this.order;
            if (comment == null) return '';

            return comment.split('\n');
        },
        menuItems() {
            const { id } = this.$route.params;
            const { permissions = {} } = this.order;

            return [
                {
                    name: 'orders-id-stylesheet',
                    label: 'crm.stylesheet.menuItem',
                    params: { id },
                    filter: () => permissions.style_sheet,
                },
                {
                    name: 'orders-id-statistic',
                    label: 'crm.statistic.menuItem',
                    params: { id },
                },
                {
                    name: 'orders-id-default-speakers',
                    label: 'crm.defaultSpeakers.menuItem',
                    params: { id },
                    filter: () => permissions.default_speakers,
                },
            ];
        },
    },

    watch: {
        'order.details.name': {
            immediate: true,
            handler(title) {
                document.title = `${title} - Speechki`;
            },
        },
        async activeTransition(transition) {
            const fields = [];

            if (!transition || !transition.schema) {
                this.additionalData = {};
            } else {
                Object.keys(transition.schema).forEach((field) => {
                    fields.push(field);
                    this.additionalData[field] = '';
                });
            }

            this.preparedOptions = {};

            if (fields.length) {
                const options = await fetchPreparedOptions(
                    transition.id,
                    fields,
                    this.$i18n.locale
                );
                this.preparedOptions = options.reduce(
                    (acc, cur) => ({ ...acc, [cur.field]: cur.variants }),
                    {}
                );
            }
        },
    },

    async created() {
        this.loading = true;
        await Promise.all([this.fetchOrder(), this.fetchOrderSchema(), this.fetchOrderHistory()]);
        this.loading = false;
    },

    mounted() {
        this.registerWsHandler('order_change_state', this.onOrderChangeState);
        this.registerWsHandler('order_complete', this.onOrderComplete);

        if (this.fetchIntervalDelay > 0) {
            this.fetchIntervalTimer = setInterval(this.fetchContent, this.fetchIntervalDelay);
        }
    },

    beforeUnmount() {
        this.removeWsHandler('order_change_state', this.onOrderChangeState);
        this.removeWsHandler('order_complete', this.onOrderComplete);
        clearInterval(this.fetchIntervalTimer);
    },

    methods: {
        ...mapActions(['pushToast']),
        formatDate,
        async fetchOrder() {
            try {
                this.order = await fetchOrder(this.$route.params.id);
            } catch {
                this.$router.replace({ name: 'orders-not-found' });
            }
        },
        async fetchOrderSchema() {
            const { partial_update } = await fetchOrderSchema();
            this.schema = partial_update;
        },
        async fetchOrderHistory() {
            this.history = await fetchOrderHistory(this.$route.params.id);
        },
        async getEmbedLink() {
            this.loading = true;
            const { token, refresh_token } = await fetchGuestToken(this.order.id);
            const comebackUrl = location.href;
            this.loading = false;

            if (!this.order.book_id) {
                this.pushToast({
                    type: 'error',
                    text: 'This order has no book. Please contact the support   ',
                });
                return;
            }

            this.embedLink = `${embedURL}/${this.order.book_id}?guest_token=${token}&refresh_token=${refresh_token}&comeback_url=${comebackUrl}`;
            await this.$nextTick();
            this.$refs.embedLinkRes.click();
        },

        async changeState(transition) {
            const { destination_state } = transition;

            let data = {
                destination_state,
                additional_data: {},
            };

            if (destination_state === 1 && this.order.state.slug === 'waiting_file') {
                if (!this.newFile) {
                    this.$refs.fileInput.focus();
                    this.pushToast({
                        type: 'error',
                        text: 'Please provide the file',
                    });
                    return;
                }
                await this.uploadNewFile(this.newFile);
                if (!this.newFileApproved) {
                    this.activeTransition = null;
                    this.pushToast({
                        type: 'error',
                        text: 'Failed to upload file',
                    });
                    return;
                }

                data = new FormData();
                data.set('document', this.newFile);
                data.set('destination_state', destination_state);
            }

            if (destination_state === 6) {
                data.additional_data = {
                    book_language: this.order.details.book_language,
                    speaker: this.order.details.speaker,
                    speed: this.order.details.speed,
                };
            }

            if (transition.schema != null) {
                data.additional_data = {
                    ...data.additional_data,
                    ...this.additionalData,
                    ...Object.keys(this.additionalData).reduce((acc, cur) => {
                        const { [cur]: curData } = this.additionalData;
                        const data = [...this.additionalDataVariants, curData];
                        return {
                            ...acc,
                            [cur]: data.join('\n'),
                        };
                    }, {}),
                };
            }

            this.stateLoading = true;
            this.refetchedContent = true;

            try {
                await changeState(this.order.id, data);
                await Promise.all([this.fetchOrder(), this.fetchOrderHistory()]);
            } catch (e) {
                console.log(e);
                this.pushToast({
                    type: 'error',
                    text: 'Error changing state',
                });
            } finally {
                this.stateLoading = false;
                this.activeTransition = null;
            }
        },

        hasTransition(destination) {
            return this.order.transitions.find((tr) => tr.destination_state === destination);
        },

        setFile(file) {
            this.newFile = file;
            this.newFileApproved = false;
        },
        async updateOrder() {
            this.loading = true;
            this.editMode = false;
            await this.fetchOrder();
            this.loading = false;
        },
        async uploadNewFile(event) {
            try {
                this.loading = true;

                const data = new FormData();

                if (typeof event === 'string') {
                    data.set('url', event);
                } else {
                    data.set('document', event);
                }

                await updateOrder(this.order.id, data);

                this.newFileApproved = true;
            } catch (e) {
                console.log(e);
            } finally {
                this.loading = false;
            }
        },

        async fetchContent(quiet = true) {
            if (!quiet) this.loading = true;
            try {
                await Promise.all([this.fetchOrder(), this.fetchOrderHistory()]);
            } catch (err) {
                console.log(err);
            } finally {
                this.loading = false;
            }
        },

        async onOrderChangeState({ code, params, errors }) {
            const {
                order: { id = null },
            } = params;

            if (id !== this.order.id) return;
            if (code !== 200) {
                console.log(errors);
                return;
            }
            if (this.refetchedContent) {
                this.refetchedContent = false;
                return;
            }

            await this.fetchContent(false);
        },

        async onOrderComplete({ code, params, errors }) {
            const {
                order: { id = null },
            } = params;

            if (id !== this.order.id) return;
            if (code !== 200) {
                console.log(errors);
                return;
            }

            await this.fetchContent(false);
        },
    },
};
</script>

<style lang="scss" src="./OrderDetailPage.scss"></style>
